import { UI_CONFIG } from "./constants";

export interface PlayerOptions {
    channels?: number;
    sampleRate?: number;
    flushingTime?: number;
}

export class AudioPlayer {
    private option: PlayerOptions;
    private samples: Float32Array;
    private interval: number | null = null;
    private audioCtx!: AudioContext;
    private startTime!: number;

    constructor(option: PlayerOptions = {}) {
        const defaults: PlayerOptions = {
            channels: 1,
            sampleRate: 8000,
            flushingTime: 1000,
        };
        this.option = { ...defaults, ...option };
        this.samples = new Float32Array();
        this.flush = this.flush.bind(this);
        this.interval = window.setInterval(this.flush, this.option.flushingTime);
        this.createContext();
    }

    private createContext(): void {
        this.audioCtx = new AudioContext();
        this.audioCtx.resume();
        this.startTime = this.audioCtx.currentTime;
    }

    private isTypedArray(data: any): boolean {
        return data.byteLength && data.buffer && data.buffer.constructor === ArrayBuffer;
    }

    public feed(data: Float32Array): void {
        if (!this.isTypedArray(data)) return;
        const tmp = new Float32Array(this.samples.length + data.length);
        tmp.set(this.samples, 0);
        tmp.set(data, this.samples.length);
        this.samples = tmp;
    }

    public destroy(): void {
        if (this.interval) {
            clearInterval(this.interval);
        }
        this.samples = new Float32Array();
        this.audioCtx.close();
    }

    private flush(): void {
        if (!this.samples.length) return;
        const bufferSource = this.audioCtx.createBufferSource();
        const length = this.samples.length / (this.option.channels || 1);
        const audioBuffer = this.audioCtx.createBuffer(this.option.channels!, length, this.option.sampleRate!);

        for (let channel = 0; channel < this.option.channels!; channel++) {
            const audioData = audioBuffer.getChannelData(channel);
            let offset = channel;
            let decrement = UI_CONFIG.FADE_SAMPLES;
            for (let i = 0; i < length; i++) {
                audioData[i] = this.samples[offset];
                if (i < UI_CONFIG.FADE_SAMPLES) {
                    audioData[i] *= i / UI_CONFIG.FADE_SAMPLES;
                }
                if (i >= length - (UI_CONFIG.FADE_SAMPLES + 1)) {
                    audioData[i] *= decrement-- / UI_CONFIG.FADE_SAMPLES;
                }
                offset += this.option.channels!;
            }
        }

        if (this.startTime < this.audioCtx.currentTime) {
            this.startTime = this.audioCtx.currentTime;
        }

        bufferSource.buffer = audioBuffer;
        bufferSource.connect(this.audioCtx.destination);
        bufferSource.start(this.startTime);
        this.startTime += audioBuffer.duration;
        this.samples = new Float32Array();
    }
}
